import { useState } from "react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const hasPassword = user?.has_password !== false; // Google-created accounts: "set a password" mode
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm bg-card border-border sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{hasPassword ? "Change password" : "Set a password"}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {hasPassword
              ? "No email provider is configured, so password recovery is not available — keep your new password safe."
              : "You signed in with Google, so this account has no password yet. Set one to also enable email/password sign-in."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {hasPassword && (
            <div className="space-y-1.5">
              <Label className="overline-label">Current password</Label>
              <Input type="password" required className="rounded-sm bg-background font-mono"
                data-testid="current-password-input" value={current} onChange={(e) => setCurrent(e.target.value)} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="overline-label">New password (min 6 chars)</Label>
            <Input type="password" required minLength={6} className="rounded-sm bg-background font-mono"
              data-testid="new-password-input" value={next} onChange={(e) => setNext(e.target.value)} />
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-sm bg-primary hover:bg-primary/85 text-white"
            data-testid="change-password-submit-button">
            {hasPassword ? "Update password" : "Set password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
